import { type FunctionComponent } from 'react';
import { useState } from 'react';
import { type InfiniteData, useSuspenseInfiniteQuery } from '@tanstack/react-query';

import { fetchMoreArticlesData, getMoreArticlesCacheKey, type MoreArticlesCacheKey } from './api/cx-api.service';
import { MoreArticles } from './components/more-articles';
import { type ArticleItem } from './types/article.types';
import { type MoreArticlesProps } from './types';

const MoreArticlesWrapper: FunctionComponent<MoreArticlesProps> = ({
  articlesPage,
  dataApiEndpoint,
  defaultFeaturedArticle,
  deviceType,
  featuredArticleId,
  isLoggedIn,
  moreArticlesPath,
  moreArticlesText,
  title,
  ...requestParams
}) => {
  const [articlesTotal, setArticlesTotal] = useState(100); // this is a mock value for testing which is updated when the data is fetched
  const validatedArticlesPage = Number.isFinite(articlesPage) && articlesPage > 0 ? articlesPage : 1;
  const queryKey = getMoreArticlesCacheKey(requestParams);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery<
    ArticleItem[],
    Error,
    InfiniteData<ArticleItem[]>,
    MoreArticlesCacheKey,
    number
  >({
    getNextPageParam(lastPage, allPages) {
      if (!lastPage || lastPage.length < requestParams.limit) {
        return undefined;
      }
      return allPages.length;
    },
    initialPageParam: validatedArticlesPage - 1,
    queryFn: async ({ pageParam }) => {
      const data = await fetchMoreArticlesData(
        dataApiEndpoint,
        {
          ...requestParams,
          offset: pageParam * requestParams.limit,
        },
        {
          logger: console,
        },
      );

      if (!data) return [];

      setArticlesTotal(data.total);

      return data.articles;
    },
    queryKey,
    refetchOnMount: requestParams.pagination === 'BOTTOM_PAGE_NUMBERS' ? 'always' : false, // this is to ensure that we get the articles total
  });

  if (!data || data.pages.flat().length === 0) return null;

  const rawFlattened = data.pages.flat() as (ArticleItem | null)[];
  const flattenedArticles = rawFlattened.filter((article): article is ArticleItem => article !== null);

  return (
    <MoreArticles
      articles={flattenedArticles}
      articlesPage={validatedArticlesPage}
      baseUrl={requestParams.baseUrl}
      defaultFeaturedArticle={defaultFeaturedArticle}
      deviceType={deviceType}
      featuredArticleId={featuredArticleId}
      hasNextPage={hasNextPage}
      isLoading={isFetchingNextPage}
      isLoggedIn={isLoggedIn}
      limit={requestParams.limit}
      loadMoreArticles={fetchNextPage}
      moreArticlesPath={moreArticlesPath}
      moreArticlesText={moreArticlesText}
      offset={requestParams.offset}
      pagination={requestParams.pagination}
      repub={requestParams.repub}
      requestUrl={requestParams.requestUrl}
      title={title}
      total={articlesTotal}
      type={requestParams.type}
    />
  );
};

export default MoreArticlesWrapper;
